import Bugsnag from '@bugsnag/js';
import Helper from '@ember/component/helper';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import {
  type AppliedPropertyFilter,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';

// this expects the queryParameter on the controller or route to be named 'filters'
//
// example
// <LinkTo @query={{filter-query name='Paul' lastName='Yoder'}} />
// this will apply 2 filters: [
//  { propertyName: 'Paul', operation: '=' val: 'Paul'},
//  { propertyName: 'lastName', operation: '=' val: 'Yoder'}
// ]
// if the propertyName has a space, then you must use the (filter-operation) helper
//
// when the operation is 'equals' then you can just use a name/value pair
// <LinkTo @query={{filter-query name='Paul'}} />
// filters = [{ propertyName: 'name', operation: '=' val: 'Paul'}]
//
// for all operations other than '=', use the {{filter helper}} in the positional position
// <LinkTo @query={{filter-query (filter 'contact_group' operation='any' val=(array 'Friend' 'Church')) name='Paul'}} />
// filters = [
//   { propertyName: 'contact_group', operation: 'any' val: ['Friend', 'Church']}
//   { propertyName: 'name', operation: 'equals' val: 'paul'}
// ]
//
// you can also pass in an additionalQueryHash to add additional query parameters
// <LinkTo @query={{filter-query name='Paul' additionalQueryHash=(hash page=2)}} />
export default class FilterQueryHelper extends Helper {
  @service declare router: RouterService;

  compute(appliedPropertyFilters: AppliedPropertyFilter[], namedArgs: any) {
    let filters: AppliedPropertyFilter[] = [];
    let additionalQueryHash = { ...(namedArgs.additionalQueryHash || {}) };

    for (let filter of [...appliedPropertyFilters]) {
      filters.push({
        propertyName: filter.propertyName,
        operation: filter.operation,
        val: filter.val,
      });
    }

    for (let propertyName in { ...namedArgs }) {
      if (propertyName === 'additionalQueryHash') {
        continue;
      }
      if (typeof namedArgs[propertyName] === 'object') {
        filters.push({ ...namedArgs[propertyName] });
      } else {
        filters.push({
          propertyName,
          operation: '=',
          val: namedArgs[propertyName],
        });
      }
    }

    if (filters.some((f) => f?.propertyName == null)) {
      // Tracking an issue where the filter-query helper has a filter in the array
      // that is an empty object (null propertyName).  This is causing the page to
      // not load properly.  We need to track this down and fix it.

      const error = new Error(
        'Tracking: filter-query helper has a filter with a null propertyName',
      );
      const custom = {
        route: this.router.currentRouteName,
        params: this.router.currentRoute?.params,
        appliedPropertyFilters: [...appliedPropertyFilters],
        namedArgs: { ...namedArgs },
        additionalQueryHash,
        filters,
      };

      console.error(error, custom);

      if (Bugsnag?.isStarted()) {
        Bugsnag.notify(error, (event) => {
          event.addMetadata('custom', custom);
        });
      }
    }

    return {
      filters: serializeAppliedFilters(filters),
      ...additionalQueryHash,
    };
  }
}
